/**
 * Container for one row in the staged-files list.
 *
 * Picks the row icon from the MIME type and binds the row's id into the remove
 * handler, so `ContractFileListItemView` receives a plain icon and a
 * zero-argument callback and derives nothing itself (SE-8019).
 * @module features/contracts/components/molecules/ContractFileListItem/ContractFileListItemContainer
 */

import { File, FileImage, FileText } from "lucide-react-native";
import React, { useCallback } from "react";

import ContractFileListItemView from "./ContractFileListItemView";

/** Props for {@link ContractFileListItemContainer}. */
export interface ContractFileListItemContainerProps {
  readonly id: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly mimeType: string;
  readonly disabled?: boolean;
  readonly onRemove: (id: string) => void;
}

/**
 * Picks an appropriate lucide icon for the given MIME type.
 * @param mimeType - The file's MIME type
 * @returns The icon component to render
 */
function pickIcon(mimeType: string): React.ElementType {
  if (mimeType === "application/pdf") {
    return FileText;
  }
  if (mimeType.startsWith("image/")) {
    return FileImage;
  }
  return File;
}

/**
 * Binds the row's identity and renders the staged-file row.
 * @param props - Row props
 * @param props.id - Stable identifier for React keys + the remove handler
 * @param props.fileName - File name to display
 * @param props.fileSize - Byte size to display (formatted via formatFileSize)
 * @param props.mimeType - MIME type used to pick the row icon
 * @param props.disabled - When true, the remove button is disabled
 * @param props.onRemove - Invoked with the row id when the user removes it
 * @returns The bound file list row
 */
function ContractFileListItemContainer({
  id,
  fileName,
  fileSize,
  mimeType,
  disabled = false,
  onRemove,
}: ContractFileListItemContainerProps) {
  const handleRemove = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  return (
    <ContractFileListItemView
      id={id}
      fileName={fileName}
      fileSize={fileSize}
      icon={pickIcon(mimeType)}
      disabled={disabled}
      onRemovePress={handleRemove}
    />
  );
}

export default ContractFileListItemContainer;
